import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { ChevronDown, Menu, Heart, LogIn, LayoutDashboard } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import FlyerPopup from "@/components/FlyerPopup";

export const NAV = [
  { label: "Mission", items: [["Rakim's Story", "/story"], ["Gallery", "/gallery"], ["Drowning Data Center", "/data"], ["Six Pillars & Circle of Protection", "/programs"], ["Impact Dashboard", "/impact"]] },
  { label: "Ponds", items: [["Report a Pond", "/report"], ["Pond Safety Map", "/map"], ["One Pond at a Time", "/programs#protect"]] },
  { label: "Sponsor", items: [["Sponsor Marketplace", "/sponsor"], ["Sponsor a Child", "/sponsor?type=child"], ["Sponsor a Fence", "/sponsor?type=fence"], ["Rakim Swim Scholarship", "/sponsor#scholarships"]] },
  { label: "Learn", items: [["Water Safety Education", "/learn"], ["Kids Center", "/learn?tab=kids"], ["Parent Center", "/learn?tab=parents"], ["Rakim's Promise & Pledge", "/promise"]] },
  { label: "Community", items: [["Events & Safety Days", "/events"], ["Annual Walk", "/events#walk"], ["Volunteer", "/volunteer"], ["Become a Partner", "/partners"]] },
];

function DesktopNav() {
  const navigate = useNavigate();
  return (
    <nav className="hidden xl:flex items-center gap-1">
      {NAV.map((g) => (
        <DropdownMenu key={g.label}>
          <DropdownMenuTrigger data-testid={`nav-${g.label.toLowerCase()}`} className="flex items-center gap-1 px-3 py-2 rounded-full text-sm font-bold uppercase tracking-wide text-[#03045E] hover:bg-sky-100 transition-colors duration-200 outline-none">
            {g.label} <ChevronDown className="w-3.5 h-3.5" />
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-64 rounded-xl">
            {g.items.map(([l, to]) => (
              <DropdownMenuItem key={to} data-testid={`nav-link-${to.replace(/[^a-z]/g, "-")}`} onClick={() => navigate(to)} className="cursor-pointer py-2.5 font-medium">{l}</DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ))}
    </nav>
  );
}

function MobileNav({ user }) {
  const [open, setOpen] = useState(false);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger className="xl:hidden p-2 rounded-full hover:bg-sky-100 shrink-0" data-testid="mobile-menu-button" aria-label="Open menu"><Menu className="w-6 h-6 text-[#03045E]" /></SheetTrigger>
      <SheetContent side="right" className="overflow-y-auto w-[90vw] max-w-sm sm:max-w-sm px-5 sm:px-6" data-testid="mobile-menu">
        <SheetTitle className="font-display uppercase text-[#03045E]">Menu</SheetTitle>
        <div className="mt-4 space-y-5">
          {NAV.map((g) => (
            <div key={g.label}>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#0096C7] mb-1">{g.label}</p>
              {g.items.map(([l, to]) => (
                <Link key={to} to={to} onClick={() => setOpen(false)} className="block py-2.5 pr-2 font-semibold text-slate-800 leading-snug break-words whitespace-normal hover:text-[#0077B6]" data-testid={`mobile-nav-${to.replace(/[^a-z]/g, "-")}`}>{l}</Link>
              ))}
            </div>
          ))}
          <Link to={user ? "/portal" : "/login"} onClick={() => setOpen(false)} className="block rounded-full bg-[#03045E] text-white text-center py-3 font-bold uppercase" data-testid="mobile-nav-portal">{user ? "My Portal" : "Partner Login"}</Link>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Footer() {
  return (
    <footer className="bg-[#03045E] text-sky-100 relative grain">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 grid gap-10 md:grid-cols-4">
        <div className="md:col-span-2">
          <img src="/brand/logo.webp" alt="Swim for Rakim logo" className="w-24 h-24 rounded-full bg-white p-1" />
          <p className="font-display uppercase text-2xl text-white mt-4 leading-tight">One Pond. One Fence. One Child. One Community. One Life at a Time.™</p>
          <p className="font-script text-2xl text-sky-300 mt-3">Every child deserves the opportunity to learn to swim.</p>
        </div>
        {NAV.slice(1, 3).concat([NAV[4]]).slice(0, 2).map((g) => (
          <div key={g.label}>
            <p className="font-display uppercase text-white text-lg">{g.label}</p>
            {g.items.map(([l, to]) => <Link key={to} to={to} className="block py-1.5 text-sm hover:text-white">{l}</Link>)}
          </div>
        ))}
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5 text-xs text-sky-200/80 flex flex-col md:flex-row gap-2 justify-between">
          <span>© {new Date().getFullYear()} Swim for Rakim™. Water Safety • Drowning Prevention • Family Education • Hope</span>
          <span>Reporting a pond does not establish that it is unsafe or legally requires fencing. Swim lessons do not replace supervision.</span>
        </div>
      </div>
      <div className="border-t border-white/10 bg-black/20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6 text-xs leading-relaxed text-sky-100/90 space-y-2" data-testid="footer-ministry-notice">
          <p className="font-bold text-white">Swim for Rakim™ is an auxiliary ministry of Life After The Storm Ministries Inc, a 508(c)(1)(A) faith-based ministry.</p>
          <p>Swim for Rakim™ is a faith-based charitable ministry organized and operated for religious, charitable, and educational purposes. Contributions are intended to be tax-deductible to the extent permitted by applicable federal and state law. Please consult your tax adviser regarding the deductibility of your contribution.</p>
        </div>
      </div>
    </footer>
  );
}

export default function Layout() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen flex flex-col overflow-x-clip">
      <div className="bg-[#03045E] text-white text-[10px] sm:text-xs font-bold uppercase tracking-[0.1em] sm:tracking-[0.2em] leading-relaxed text-center py-2 px-3" data-testid="top-ribbon">
        Protect the water's edge • Educate the child • Prepare the family • Mobilize the community
      </div>
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-sky-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-20 flex items-center justify-between gap-2 sm:gap-4">
          <Link to="/" className="flex items-center gap-3 min-w-0" data-testid="header-logo-link">
            <img src="/brand/logo.webp" alt="Swim for Rakim" className="w-12 h-12 sm:w-14 sm:h-14 object-contain shrink-0" />
            <div className="leading-none hidden sm:block">
              <p className="font-display uppercase font-bold text-xl text-[#03045E]">Swim for Rakim™</p>
              <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#0096C7] mt-1">One Pond at a Time™</p>
            </div>
          </Link>
          <DesktopNav />
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <NavLink to={user ? "/portal" : "/login"} data-testid="header-portal-link" className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold text-[#03045E] hover:bg-sky-100 transition-colors duration-200">
              {user ? <LayoutDashboard className="w-4 h-4" /> : <LogIn className="w-4 h-4" />} {user ? "Portal" : "Login"}
            </NavLink>
            <Link to="/sponsor" data-testid="header-donate-button" className="inline-flex items-center gap-1.5 rounded-full bg-[#FF6B35] hover:bg-[#e85a26] text-white px-4 sm:px-5 py-2.5 text-sm font-bold uppercase tracking-wide whitespace-nowrap transition-colors duration-200">
              <Heart className="w-4 h-4" /> Donate
            </Link>
            <MobileNav user={user} />
          </div>
        </div>
      </header>
      <main className="flex-1"><Outlet /></main>
      <Footer />
      <FlyerPopup />
    </div>
  );
}
